<script lang="ts">
    import type {LoanRequestOption, StandardReservability} from 'src/typings';
    import {getInjector, getLocalization, getSanitize} from 'src/core';
    import {LoanRequestFormService} from '../loan-request-form.service';
    import KpGenericPanel from 'shared/ui-widgets/panel/KpGenericPanel.svelte';
    import KpChipTag from 'shared/ui-widgets/chip/KpChipTag.svelte';
    import KpPluralizeText from 'shared/components/kp-pluralize-text/KpPluralizeText.svelte';
    import KpUniversalForm from 'shared/value-editors/kp-universal-form/KpUniversalForm.svelte';
    import type {Field} from 'node_modules/svelte-forms/types';
    import type {FormGroup} from 'shared/value-editors/internal/forms/form-group';
    import {exists, isNullOrUndefined} from 'shared/utils/custom-utils';
    import LoginFirstButton from './shared/LoginFirstButton.svelte';
    import SendLoanRequestButton from './shared/SendLoanRequestButton.svelte';
    import DesiredBuilding from './shared/DesiredBuilding.svelte';
    import BuildingsList from './shared/BuildingsList.svelte';
    import LoanOptionPanelBody from './shared/LoanOptionPanelBody.svelte';
    import FullWidthBox from './shared/FullWidthBox.svelte';
    import type {Observable} from 'rxjs';

    export let option: LoanRequestOption<StandardReservability>;

    const localize = getLocalization();
    const sanitize = getSanitize();
    const service = getInjector().getByClass(LoanRequestFormService);
    const additionalInfoText = localize(/* @kp-localization loan.standardReservability.additionalModalInfo */ 'loan.standardReservability.additionalModalInfo');

    $: showLoginButtonFirst = service.requiresLogin(option);

    let submitted = false;
    let getFormController: () => FormGroup<any>;
    let formState$: Observable<Field<any>>;

    $: {
        if (exists(getFormController)) {
            formState$ = getFormController().getFieldState$();
        }
    }

    function isInvalidRequest(request: typeof option.formObject): boolean {
        return isNullOrUndefined(request) || request.desiredBuildings.length === 0;
    }
</script>

<KpGenericPanel>
    <svelte:fragment slot="heading">
        <strong>{localize(/* @kp-localization loan.Rezervace */ 'loan.Rezervace')}</strong>
        <KpChipTag chipSize="sm" chipStyle="info">{localize(/* @kp-localization loan.Absencni */ 'loan.Absencni')}</KpChipTag>
    </svelte:fragment>

    <LoanOptionPanelBody>
        <div>
            {#if option.requestItems.length === 1}
                <KpPluralizeText count={option.capability.buildings.length}
                                 oneCountText={localize(/* @kp-localization loan.DokumentJeKZarezervovaniNaBudove */ 'loan.DokumentJeKZarezervovaniNaBudove')}
                                 moreText={localize(/* @kp-localization loan.DokumentJeKZarezervovaniNaBudovach */ 'loan.DokumentJeKZarezervovaniNaBudovach')}/>
            {:else if option.requestItems.length > 1}
                <KpPluralizeText count={option.capability.buildings.length}
                                 oneCountText={localize(/* @kp-localization loan.DokumentyJsouKZarezervovaniNaBudove */ 'loan.DokumentyJsouKZarezervovaniNaBudove')}
                                 moreText={localize(/* @kp-localization loan.DokumentyJsouKZarezervovaniNaBudovach */ 'loan.DokumentyJsouKZarezervovaniNaBudovach')}/>
            {/if}
        </div>

        {#if additionalInfoText.length > 0}
            <div class="loan-additional-info">
                {@html sanitize(additionalInfoText)}
            </div>
        {/if}

        {#if showLoginButtonFirst}
            <BuildingsList {option}/>
            <LoginFirstButton/>
        {:else}
            <DesiredBuilding capability={option.capability} building={option.formObject.desiredBuildings.at(0)}/>

            <FullWidthBox>
                <KpUniversalForm formSettings={option.form}
                                 model={option.formObject}
                                 forceShowErrors={submitted}
                                 bind:getFormController={getFormController}
                                 on:submit={() => submitted = true}/>
            </FullWidthBox>

            <SendLoanRequestButton {option}
                                   apiPath="/loan-items/request/standardReservationRequest"
                                   isDisabled={isInvalidRequest(option.formObject) || $formState$?.invalid}
                                   dataQa="standard-reservation-button">
                {localize(/* @kp-localization commons.rezervovat */ 'commons.rezervovat')}
            </SendLoanRequestButton>
        {/if}
    </LoanOptionPanelBody>
</KpGenericPanel>